import { createReducer, combineReducers } from '@reduxjs/toolkit'
import { extend, includes } from 'lodash'

import GlobalActionTypes from '@constants/action_types.js'
import { paginate } from '@reducers/data/paginate.js'

import Types from '../types.js'

// initial state under the base key
export const initialBaseState = {
  pageNumber: 1,
  photoDeletions: [],
}

const base = createReducer(initialBaseState, builder => {
  builder
    .addCase(Types.PAGINATE, (state, action) => paginateTo(state, action))
    .addCase(Types.PHOTO_DELETE_REQUEST, (state, action) => photoDeleteRequestStart(state, action))
    .addCase(Types.PHOTO_UPLOAD_START, (state, action) => photoUploadRequestStart(state, action))
    .addMatcher(
      ({ type }) => includes([Types.PHOTO_UPLOAD_SUCCESS, Types.PHOTO_UPLOAD_FAILURE], type),
      (state, action) => photoUploadRequestFinish(state, action)
    )
    .addMatcher(
      ({ type }) => includes([Types.PHOTO_DELETE_SUCCESS, Types.PHOTO_DELETE_FAILURE], type),
      (state, action) => photoDeleteRequestFinish(state, action)
    )
})

function paginateTo(state, action) {
  const { payload: pageNumber } = action
  state.pageNumber = pageNumber
}

function photoUploadRequestStart(state, action) {
  state.uploading = true
  return state
}

function photoUploadRequestFinish(state, action) {
  state.uploading = false
  return state
}

function photoDeleteRequestStart(state, action) {
  const { payload: photoId } = action
  state.photoDeletions.push(photoId)
  return state
}

function photoDeleteRequestFinish(state, action) {
  const { payload: photoId } = action
  const nextArray = state.photoDeletions.filter(id => id !== photoId)
  state.photoDeletions = nextArray
}

export default combineReducers({
  base,
  pagination: paginate({
    mapActionToKey: action => {
      const { type, payload } = action
      // Note: If deleted a photo, we need to run the function over the pagination root slice, instead of a particular page slice
      if (type === GlobalActionTypes.PHOTO_DELETE_SUCCESS) {
        return ''
      } else {
        return payload.pageNumber.toString()
      }
    },
    types: [
      Types.USER_PHOTOS_REQUEST,
      Types.USER_PHOTOS_SUCCESS,
      Types.USER_PHOTOS_FAILURE,
      GlobalActionTypes.PHOTO_DELETE_SUCCESS,
    ],
  }),
})

// selectors
const getRoot = state => state.photoLibrary
const getBase = state => getRoot(state).base
const getPaginationBase = state => getRoot(state).pagination

export const getCurrentPage = state => getBase(state).pageNumber

export function getPagination(state, props) {
  const { page } = props
  const lastTotals = getLastPaginationTotals(state)
  const paginationBase = getPaginationBase(state)
  let pagination = paginationBase[page.toString()]
  if (lastTotals && !pagination) {
    // no pagination for page, augment with previous totals so we can show pagination in the view
    pagination = extend({}, lastTotals, { currentPage: page })
  } else if (lastTotals && pagination && !pagination.hasFetchedBefore) {
    // first time fetching for this page, augment with previous totals so we can show pagination in the view
    pagination = extend({}, pagination, lastTotals, { currentPage: page })
  }
  return pagination
}

export function getLastPaginationTotals(state) {
  return getPaginationBase(state).lastTotals
}

export function getUploading(state) {
  return getBase(state).uploading
}

export function getPhotoDeletions(state) {
  return getBase(state).photoDeletions
}
